// OrgSettings.js
//
// Build 25.1: organization-wide settings page at /admin/org-settings.
//
// Currently configurable:
//   - Organization name
//   - Which suite apps are enabled for the organization
//
// All changes require the admin to type their own email to confirm
// (friction-as-safety). All changes are recorded to orgSettingsAudit.
//
// Effective access for any user becomes: org_enabled(app) AND user_assigned(app).
// Admins are subject to the same check, so an admin can clear their own
// dashboard by toggling apps off here OR by editing their personal
// assignedSuiteApps in AdminPanel.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { SUITE_APPS } from './constants/appCatalog';
import {
  ensureOrgSettings, updateOrgSetting, getRecentAudit
} from './services/orgSettingsService';
import './OrgSettings.css';

const ORG_DOC_ID = 'orgSettings';

function OrgSettings({ user, userProfile }) {
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Local edit state — only commits to Firestore via Save buttons
  const [enabledIds, setEnabledIds] = useState([]);
  const [orgName, setOrgName] = useState('');

  // Confirmation modal state
  const [pendingChange, setPendingChange] = useState(null); // { field, newValue, oldValue }
  const [confirmInput, setConfirmInput] = useState('');

  // Audit log preview
  const [recentAudit, setRecentAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  useEffect(() => {
    document.title = 'Organization Settings - Wixon & Cross Desk Suite';
  }, []);

  // Block non-admins
  useEffect(() => {
    if (userProfile && userProfile.role !== 'Admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [userProfile, navigate]);

  // Make sure the doc exists before our listener kicks in
  useEffect(() => {
    if (!user || userProfile?.role !== 'Admin') return;
    ensureOrgSettings().catch(err => {
      console.error('ensureOrgSettings failed in OrgSettings:', err);
    });
  }, [user, userProfile]);

  // Listen to org settings doc
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'system', ORG_DOC_ID), (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setSettings(data);
        setEnabledIds(Array.isArray(data.enabledAppIds) ? data.enabledAppIds : []);
        setOrgName(data.orgName || '');
      } else {
        setSettings(null);
        setEnabledIds([]);
        setOrgName('');
      }
      setLoading(false);
    }, (err) => {
      console.error('OrgSettings snapshot error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  // Load recent audit
  const refreshAudit = async () => {
    setAuditLoading(true);
    const entries = await getRecentAudit(5);
    setRecentAudit(entries);
    setAuditLoading(false);
  };
  useEffect(() => {
    if (!user || userProfile?.role !== 'Admin') return;
    refreshAudit();
  }, [user, userProfile]);

  // ── Edit helpers ────────────────────────────────────────────────────────
  const toggleApp = (appId) => {
    setEnabledIds(prev => prev.includes(appId)
      ? prev.filter(a => a !== appId)
      : [...prev, appId]
    );
  };

  const selectAll = () => setEnabledIds(SUITE_APPS.map(a => a.id));
  const clearAll = () => setEnabledIds([]);

  const hasAppChanges = settings && !arraysEqual(
    [...enabledIds].sort(),
    [...(settings.enabledAppIds || [])].sort()
  );

  const hasNameChange = settings && orgName.trim() !== (settings.orgName || '');

  // ── Save flow ──────────────────────────────────────────────────────────
  const startSaveApps = () => {
    setPendingChange({
      field: 'enabledAppIds',
      newValue: enabledIds,
      oldValue: settings?.enabledAppIds || [],
      humanLabel: 'enabled applications'
    });
    setConfirmInput('');
  };

  const startSaveName = () => {
    const trimmed = orgName.trim();
    if (!trimmed) {
      alert('Organization name cannot be empty.');
      return;
    }
    setPendingChange({
      field: 'orgName',
      newValue: trimmed,
      oldValue: settings?.orgName || '',
      humanLabel: 'organization name'
    });
    setConfirmInput('');
  };

  const cancelPending = () => {
    setPendingChange(null);
    setConfirmInput('');
  };

  const commitPending = async () => {
    if (!pendingChange) return;
    if (confirmInput.trim().toLowerCase() !== (user.email || '').toLowerCase()) {
      alert('Confirmation text does not match your email.');
      return;
    }
    setSaving(true);
    try {
      await updateOrgSetting({
        field: pendingChange.field,
        newValue: pendingChange.newValue,
        oldValue: pendingChange.oldValue,
        user,
        confirmedWith: confirmInput.trim()
      });
      setPendingChange(null);
      setConfirmInput('');
      refreshAudit();
    } catch (err) {
      console.error('updateOrgSetting failed:', err);
      alert(`Save failed: ${err?.code || err?.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="org-settings-page">
        <p style={{ padding: '2rem', textAlign: 'center' }}>Loading organization settings…</p>
      </div>
    );
  }

  return (
    <div className="org-settings-page">
      <header className="org-settings-header">
        <div className="org-settings-header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>🖥️ Desktop</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>📦 Dashboard</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/admin')}>⚙️ Admin</button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">🏢 Organization Settings</span>
          </div>
          <div className="org-settings-user-info">
            <span className="user-email">{user.email}</span>
            <span className="role-badge">Admin</span>
          </div>
        </div>
      </header>

      <main className="org-settings-main">
        <h1 className="org-settings-title">Organization Settings</h1>
        <p className="org-settings-subtitle">
          Changes here affect every user in the organization, including other admins.
          Save requires you to type your email to confirm.
        </p>

        {/* Org name */}
        <section className="org-settings-section">
          <h2>Organization Name</h2>
          <p className="section-help">Displayed in the suite. Currently used for branding only.</p>
          <div className="org-name-row">
            <input
              type="text"
              className="org-name-input"
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              placeholder="Organization name"
            />
            <button
              className="btn btn-primary"
              onClick={startSaveName}
              disabled={!hasNameChange || saving}
            >
              Save Name
            </button>
          </div>
        </section>

        {/* Enabled apps */}
        <section className="org-settings-section">
          <h2>Enabled Applications</h2>
          <p className="section-help">
            Apps unchecked here are hidden from every user's dashboard and route URLs,
            regardless of their per-user assignments. {enabledIds.length} of {SUITE_APPS.length} enabled.
          </p>
          <div className="apps-toolbar">
            <button className="btn btn-small btn-secondary" onClick={selectAll}>Select All</button>
            <button className="btn btn-small btn-secondary" onClick={clearAll}>Clear All</button>
          </div>
          <div className="apps-grid">
            {SUITE_APPS.map(app => (
              <label key={app.id} className={`app-row ${enabledIds.includes(app.id) ? 'app-row--enabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={enabledIds.includes(app.id)}
                  onChange={() => toggleApp(app.id)}
                />
                <span className="app-icon">{app.icon}</span>
                <span className="app-info">
                  <span className="app-name">{app.name}</span>
                  <span className="app-desc">{app.description}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="apps-save-row">
            <button
              className="btn btn-primary"
              onClick={startSaveApps}
              disabled={!hasAppChanges || saving}
            >
              Save Enabled Apps
            </button>
            {hasAppChanges && (
              <button
                className="btn btn-secondary"
                onClick={() => setEnabledIds(settings?.enabledAppIds || [])}
                disabled={saving}
              >
                Discard changes
              </button>
            )}
          </div>
        </section>

        {/* Audit log preview */}
        <section className="org-settings-section">
          <div className="audit-header">
            <h2>Recent Changes</h2>
            <button
              className="btn btn-small btn-secondary"
              onClick={() => navigate('/admin/org-settings/audit')}
            >
              See full history →
            </button>
          </div>
          {auditLoading ? (
            <p>Loading audit log…</p>
          ) : recentAudit.length === 0 ? (
            <p className="audit-empty">No changes recorded yet.</p>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Changed</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {recentAudit.map(entry => (
                  <tr key={entry.id}>
                    <td className="audit-when">{formatTime(entry.at)}</td>
                    <td className="audit-who">{entry.userEmail}</td>
                    <td className="audit-field">{labelForField(entry.field)}</td>
                    <td className="audit-summary">{summarizeChange(entry)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>

      {/* Confirmation modal */}
      {pendingChange && (
        <div className="modal-overlay" onClick={cancelPending}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Confirm change to {pendingChange.humanLabel}</h2>
            <p className="modal-help-text">
              This change affects every user in the organization. To confirm,
              type your email address (<strong>{user.email}</strong>) below.
            </p>

            {/* Show what's actually changing */}
            <div className="change-preview">
              {pendingChange.field === 'orgName' ? (
                <>
                  <div><strong>Before:</strong> {pendingChange.oldValue || <em>(empty)</em>}</div>
                  <div><strong>After:</strong> {pendingChange.newValue}</div>
                </>
              ) : (
                <ChangeDiff oldList={pendingChange.oldValue} newList={pendingChange.newValue} />
              )}
            </div>

            <input
              type="text"
              className="confirm-input"
              value={confirmInput}
              onChange={e => setConfirmInput(e.target.value)}
              placeholder="Type your email to confirm"
              autoFocus
            />

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={cancelPending} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={commitPending}
                disabled={saving || confirmInput.trim().toLowerCase() !== (user.email || '').toLowerCase()}
              >
                {saving ? 'Saving…' : 'Confirm and save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────
function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function formatTime(ts) {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleString();
}

function labelForField(field) {
  if (field === 'enabledAppIds') return 'Enabled apps';
  if (field === 'orgName') return 'Organization name';
  return field;
}

function summarizeChange(entry) {
  if (entry.field === 'orgName') {
    return `"${entry.before || ''}" → "${entry.after || ''}"`;
  }
  if (entry.field === 'enabledAppIds') {
    const before = entry.before || [];
    const after = entry.after || [];
    const added = after.filter(a => !before.includes(a));
    const removed = before.filter(a => !after.includes(a));
    const parts = [];
    if (added.length) parts.push(`+${added.length} added`);
    if (removed.length) parts.push(`−${removed.length} removed`);
    return parts.length ? parts.join(', ') : 'no net change';
  }
  return '';
}

function ChangeDiff({ oldList, newList }) {
  const before = oldList || [];
  const after = newList || [];
  const added = after.filter(a => !before.includes(a));
  const removed = before.filter(a => !after.includes(a));
  return (
    <>
      {added.length > 0 && (
        <div><strong>Adding:</strong> {added.join(', ')}</div>
      )}
      {removed.length > 0 && (
        <div><strong>Removing:</strong> {removed.join(', ')}</div>
      )}
      {added.length === 0 && removed.length === 0 && (
        <div><em>No net change.</em></div>
      )}
    </>
  );
}

export default OrgSettings;
